import { Button } from '@/app/common/components/button/Button'
import { Card, CardContent } from '@/app/common/components/Card'
import { DatePicker } from '@/app/common/components/datepicker'
import FileUpload, {
  UploadFile,
} from '@/app/common/components/FileUpload/FileUpload'
import Image from '@/app/common/components/Image'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { static_errors } from '@/app/common/constants'
import { cn } from '@/lib/utils'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { toast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { MutationStatus } from '@tanstack/react-query'
import { format } from 'date-fns'
import { BadgeCheck, CircleOff, Loader } from 'lucide-react'
import { useState } from 'react'
import CurrencyInput from 'react-currency-input-field'
import { Controller, useForm } from 'react-hook-form'
import RichTextEditor from '../AddProduct/RichTextEditor'
import {
  ConfigureStoreEditValues,
  ConfigureStoreFormValues,
  getConfigureStoreSchema,
  getTransformedProductSchema,
  TransformedProduct,
} from './add-product.schema'
import {
  DURATION_UNIT_OPTIONS,
  ITEM_TYPE_OPTIONS,
  MARKETPLACE_STATUS_MAP,
} from './constants'

interface AddProductFormProps {
  categories: ProgramCategory[]
  onSubmit: (data: TransformedProduct) => void
  mutationStatus: MutationStatus
  initialValues?: ConfigureStoreEditValues
  isNewProduct: boolean
}

const STATUS_CARDS = [
  {
    value: MARKETPLACE_STATUS_MAP.IN_PREVIEW,
    icon: <Loader className="mb-2 text-orange-600" />,
    title: 'In Preview',
    desc: 'This item is being prepared for the marketplace and will show up in the preview but not in the marketplace',
  },
  {
    value: MARKETPLACE_STATUS_MAP.ACTIVE,
    icon: <BadgeCheck className="mb-2 text-green-600" />,
    title: 'Active',
    desc: 'This item is currently available for purchase and will show up in both preview and marketplace',
  },
  {
    value: MARKETPLACE_STATUS_MAP.INACTIVE,
    icon: <CircleOff className="mb-2 text-red-600" />,
    title: 'Inactive',
    desc: "This item is temporarily unavailable to buyers and won't show up in preview or marketplace",
  },
]
const currentYearStart = new Date(new Date().getFullYear(), 0, 1)
const tenYearsFromNow = new Date(new Date().getFullYear() + 10, 11, 31)

const ProductForm = ({
  categories,
  isNewProduct,
  onSubmit,
  mutationStatus,
  initialValues,
}: AddProductFormProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(
    initialValues &&
      'saved_image_path' in initialValues &&
      typeof initialValues.saved_image_path === 'string'
      ? initialValues.saved_image_path
      : null
  )

  const {
    register,
    handleSubmit,
    control,
    setValue,
    resetField,
    clearErrors,
    trigger,
    watch,
    getValues,
    formState: { errors },
  } = useForm<ConfigureStoreFormValues>({
    resolver: zodResolver(getConfigureStoreSchema(isNewProduct)),
    mode: 'onBlur',
    defaultValues: {
      ...initialValues,
      name: initialValues?.name || '',
      marketplace:
        initialValues?.marketplace || MARKETPLACE_STATUS_MAP.IN_PREVIEW,
    },
  })

  const handleImageUpload = (files: UploadFile[]) => {
    const file = files[0]
    console.log(file, 'here')
    console.log(file instanceof File, 'here check')
    if (file) {
      setValue('file', file.file)
      setImagePreview(file.previewUrl)
      // Trigger validation for the file field to clear any existing errors
      trigger('file')
    }
  }

  const handleRemoveImage = () => {
    setImagePreview(null)
    setValue('file', undefined)
    // Trigger validation for the file field to show error if file is required
    trigger('file')
  }

  const onFormSubmit = (data: ConfigureStoreFormValues) => {
    try {
      const backendFriendlyParse =
        getTransformedProductSchema(isNewProduct).parse(data)

      onSubmit(backendFriendlyParse)
    } catch (error) {
      toast({
        title: 'Error',
        description: static_errors.unexpected_contact_support,
        variant: 'destructive',
      })
    }
  }

  const selectedCategoryItems = categories.find((cat) => {
    return (
      cat.global_category.id.toString() ===
      getValues('global_item_category_id')?.toString()
    )
  })

  const selectedItem = selectedCategoryItems?.program_items.find((item) => {
    return item.id.toString() === getValues('program_item_id')?.toString()
  })

  watch([
    'duration_type',
    'is_subscription',
    'file',
    'start_date',
    'global_item_category_id',
    'marketplace',
    'program_item_id',
  ])

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div>
        <Label htmlFor="category" isRequired>
          Category
        </Label>
        <Controller
          name="global_item_category_id"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={(value) => {
                field.onChange(value)
                resetField('program_item_id')
              }}
              value={field.value?.toString()}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem
                    key={category.global_category.id}
                    value={category.global_category.id.toString()}
                  >
                    {category.global_category_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.global_item_category_id && (
          <p className="mt-1 text-sm text-red-600">
            {errors.global_item_category_id.message}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor="item" isRequired>
          Item
        </Label>
        <Controller
          name="program_item_id"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={(value) => {
                const globalItem = selectedCategoryItems?.program_items.find(
                  (item) => {
                    return item.id.toString() === value
                  }
                )

                if (!globalItem) {
                  throw new Error('Global item not found')
                }

                setValue('global_item_id', globalItem.global_item_id.toString())
                setValue('product_type', globalItem?.global_item.type)
                field.onChange(value)
              }}
              value={field.value?.toString()}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an item" />
              </SelectTrigger>
              <SelectContent>
                {selectedCategoryItems?.program_items.map((item) => (
                  <SelectItem key={item.id} value={item.id.toString()}>
                    {item.global_item_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.program_item_id && (
          <p className="mt-1 text-sm text-red-600">
            {errors.program_item_id.message}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="name" isRequired>
          Product Name
        </Label>
        <Input
          id="name"
          type="text"
          {...register('name')}
          placeholder="Enter product name"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="price" isRequired>
          Price
        </Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 sm:text-sm">$</span>
          </div>
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <CurrencyInput
                id="price"
                className="pl-7 block w-full border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                decimalsLimit={2}
                min={0}
                placeholder="0.00"
                prefix=""
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          />
        </div>
        {errors.price && (
          <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="salesTax">Sales Tax (%)</Label>
        <Input
          id="salesTax"
          type="number"
          step="0.01"
          min="0"
          max="100"
          {...register('sales_tax')}
          placeholder="0.00"
        />
        {errors.sales_tax && (
          <p className="mt-1 text-sm text-red-600">
            {errors.sales_tax.message}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="comments">Description</Label>
        <Controller
          name="comments"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              value={field.value || ''}
              onChange={field.onChange}
            />
          )}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* Keywords Input */}
      <div>
        <Label htmlFor="keywords">Keywords</Label>
        <Controller
          name="keyword_tags"
          control={control}
          render={({ field }) => {
            const handleKeyDown = (
              e: React.KeyboardEvent<HTMLInputElement>
            ) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (e.currentTarget.value.trim()) {
                  if (!field.value?.includes(e.currentTarget.value.trim())) {
                    field.onChange([
                      ...(field.value || []),
                      e.currentTarget.value.trim(),
                    ])
                  }
                  e.currentTarget.value = ''
                }
              }
            }

            const handleRemove = (keyword: string) => {
              field.onChange((field.value || []).filter((k) => k !== keyword))
            }
            return (
              <div>
                <Input
                  id="keywords"
                  type="text"
                  placeholder="Type a keyword and press Enter"
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {(field.value || []).map((keyword: string) => (
                    <span
                      key={keyword}
                      className="inline-flex items-center px-3 py-1 rounded-full  text-primary-800 text-xs font-medium border border-primary-200"
                    >
                      {keyword}
                      <button
                        type="button"
                        className="ml-2 text-gray-800 hover:text-primary-700 focus:outline-none"
                        onClick={() => handleRemove(keyword)}
                        aria-label={`Remove ${keyword}`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )
          }}
        />
        {errors.keyword_tags && (
          <p className="mt-1 text-sm text-red-600">
            {errors.keyword_tags.message}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="image">Product Image</Label>
        <Controller
          name="file"
          control={control}
          render={({ fieldState }) => {
            return (
              <div className="mt-2">
                <FileUpload
                  onUpload={handleImageUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {fieldState.error?.message && (
                  <p className="mt-1 text-sm text-red-600">
                    {fieldState.error?.message}
                  </p>
                )}
              </div>
            )
          }}
        />
        {imagePreview && (
          <div className="mt-4">
            <div>
              {imagePreview.startsWith('provider_items') ? (
                <Image
                  className="max-w-xs max-h-48 object-contain border border-gray-300 rounded"
                  path={imagePreview}
                />
              ) : (
                <img
                  src={imagePreview}
                  alt="Product preview"
                  className="max-w-xs max-h-48 object-contain border border-gray-300 rounded"
                />
              )}
            </div>
            {isNewProduct && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={handleRemoveImage}
              >
                Remove Image
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedItem?.global_item.type === ITEM_TYPE_OPTIONS.SERVICE && (
        <div>
          <div className="mb-6">
            <Label>Usage Quantity (Optional)</Label>
            <Input type="number" min={1} {...register('usage_quantity')} />
            {errors.usage_quantity && (
              <p className="mt-1 text-sm text-red-600">
                {errors.usage_quantity.message}
              </p>
            )}
          </div>
          <div className="mb-4">
            <div className="mb-1">
              <Label>Duration Type</Label>
            </div>
            <Controller
              name="duration_type"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="flex flex-col gap-4"
                >
                  <div className="flex items-center gap-1">
                    <RadioGroupItem
                      value="specific"
                      id="duration_type_specific"
                    />
                    <Label htmlFor="duration_type_specific">
                      Specific Duration
                    </Label>
                  </div>
                  <div className="flex items-center gap-1">
                    <RadioGroupItem value="period" id="duration_type_period" />
                    <Label htmlFor="duration_type_period">Time Period</Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.duration_type && (
              <p className="mt-1 text-sm text-red-600">
                {errors.duration_type.message}
              </p>
            )}
          </div>
          {getValues('duration_type') === 'specific' && (
            <div className="mb-4 flex gap-2">
              <div className="flex-1">
                <Label>
                  <p className="font-bold mb-1">Duration:</p>
                </Label>
                <Input
                  type="number"
                  className="shadow-none"
                  min={1}
                  {...register('specific_duration')}
                />
                {errors.specific_duration && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.specific_duration.message}
                  </p>
                )}
              </div>
              <div className="flex-1">
                <Label>
                  <p className="font-bold mb-1">Unit of Measure:</p>
                </Label>
                <Controller
                  name="duration_unit"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_UNIT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.duration_unit && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.duration_unit.message}
                  </p>
                )}
              </div>
            </div>
          )}
          {getValues('duration_type') === 'period' && (
            <div>
              <div className="flex gap-4">
                <div className="w-full">
                  <Label>Start Date</Label>
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        name="start_date"
                        minDate={currentYearStart}
                        maxDate={tenYearsFromNow}
                        initialDate={
                          field.value ? new Date(field.value) : undefined
                        }
                        onDateChange={(date: Date | undefined) => {
                          if (date) {
                            const formattedDate = format(date, 'yyyy-MM-dd')
                            setValue('start_date', formattedDate)
                            field.onChange(formattedDate)
                          }
                        }}
                      />
                    )}
                  />
                  {errors.start_date && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.start_date.message}
                    </p>
                  )}
                </div>
                <div className="w-full">
                  <Label>End Date</Label>
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        name="end_date"
                        minDate={
                          getValues('start_date')
                            ? new Date(getValues('start_date') as string)
                            : currentYearStart
                        }
                        maxDate={tenYearsFromNow}
                        initialDate={
                          field.value ? new Date(field.value) : undefined
                        }
                        onDateChange={(date: Date | undefined) => {
                          if (date) {
                            const formattedDate = format(date, 'yyyy-MM-dd')
                            setValue('end_date', formattedDate)
                            field.onChange(formattedDate)
                          }
                        }}
                        readonly={!!getValues('is_subscription')}
                      />
                    )}
                  />
                  {errors.end_date && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.end_date.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center mt-2">
                <input
                  id="is_subscription"
                  type="checkbox"
                  checked={getValues('is_subscription')}
                  onChange={(e) => {
                    setValue('is_subscription', e.target.checked)
                    clearErrors('end_date')
                    if (e.target.checked) setValue('end_date', undefined)
                  }}
                  className="mr-2 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <Label htmlFor="is_subscription">
                  No end date (Subscription)
                </Label>
              </div>
            </div>
          )}
        </div>
      )}
      <Label>Status</Label>
      <Controller
        name="marketplace"
        control={control}
        render={({ field }) => (
          <RadioGroup
            value={field.value}
            onValueChange={field.onChange}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2"
          >
            {STATUS_CARDS.map((opt) => (
              <label
                key={opt.value}
                htmlFor={opt.value.toString()}
                className="cursor-pointer h-full"
              >
                <Card
                  className={cn(
                    'h-full border shadow-none',
                    field.value === opt.value.toString()
                      ? 'border-gray-600'
                      : 'border-gray-200'
                  )}
                >
                  <CardContent className="flex flex-col shadow-none items-start justify-between h-full">
                    <RadioGroupItem
                      value={opt.value.toString()}
                      id={opt.value.toString()}
                      className="hidden"
                    />
                    {opt.icon}
                    <div>
                      <p className="font-semibold text-gray-800">{opt.title}</p>
                      <p className="text-sm text-gray-600">{opt.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </label>
            ))}
          </RadioGroup>
        )}
      />
      <div className="flex gap-4 pt-4">
        <SubmitButton
          variant="primary"
          buttonTxt="Submit"
          submittingText="Submitting..."
          status={mutationStatus}
        />
        <Button type="button" variant="outline" size="lg">
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default ProductForm
